import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Pill, CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Square hosted checkout redirects with ?orderId=... ; keep session_id fallback for legacy links.
  const sessionId = searchParams.get("orderId") || searchParams.get("session_id") || searchParams.get("checkoutId");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const { data } = await API.get(`/payments/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === "paid") {
        setStatus("success");
        try {
          const { analytics } = await import("@/instrument");
          analytics.capture("checkout_completed", {
            payment_type: data.payment_type || data.metadata?.payment_type || null,
            is_subscription: Boolean(data.subscription_id),
          });
          // GA4 standard ecommerce event — powers revenue attribution in
          // reports and campaign ROI in the Acquisition dashboards.
          const valueUsd = data.amount_total ? Number(data.amount_total) / 100 : 0;
          analytics.capture("purchase", {
            transaction_id: sessionId,
            value: valueUsd,
            currency: (data.currency || "usd").toUpperCase(),
            items: [{
              item_id: data.metadata?.medication_slug
                || data.metadata?.plan_id
                || sessionId,
              item_name: data.metadata?.medication_name
                || data.metadata?.plan_name
                || "GLPFlo order",
              item_category: data.subscription_id ? "subscription" : "one_time",
              price: valueUsd,
              quantity: 1,
            }],
          });
        } catch (_e) { /* noop */ }
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        setStatus("processing");
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch (e) {
      if (attempt < 4) {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
        </Link>

        {(status === "checking" || status === "processing") && (
          <div data-testid="payment-processing">
            <Loader2 className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Processing Payment...</h2>
            <p className="text-slate-500 text-sm">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-confirmed">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Payment Successful!</h2>
            <p className="text-slate-500 text-sm mb-2">Your order has been submitted for provider review.</p>
            {details && (
              <div className="bg-purple-50 rounded-xl p-4 mt-4 mb-6 text-left">
                <p className="text-sm text-slate-700"><span className="font-medium">Medication:</span> {details.metadata?.medication_name}</p>
                <p className="text-sm text-slate-700"><span className="font-medium">Amount:</span> ${(details.amount_total / 100).toFixed(2)}</p>
                <p className="text-sm text-slate-700"><span className="font-medium">Status:</span> <span className="text-emerald-600 font-medium">Paid</span></p>
              </div>
            )}
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 w-full" onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </div>
        )}

        {(status === "expired" || status === "error" || status === "timeout") && (
          <div data-testid="payment-failed">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Payment Issue</h2>
            <p className="text-slate-500 text-sm mb-6">{status === "expired" ? "Your payment session expired." : "We couldn't confirm your payment. Please check your dashboard."}</p>
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 w-full" onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
